// src\themes\lino\components\ProductCard.tsx
'use client';

import Image from 'next/image';
import Link from 'next/link';
import { Plus } from 'lucide-react';
import { useDictionary } from '@/core/hooks/use-dictionary';
import { MainShopProduct } from '@/core/models/shop-product-model';
import { formatPrice } from "@/utils/formatUtils"
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import ProductHoverMedia from '@/components/shared/ProductHoverMedia';

export default function ProductCard({ product, priority = false }: { product: MainShopProduct; priority?: boolean }) {
  const { dictionary } = useDictionary();

  // اولویت با thumbnail_hover (که می‌تونه عکس یا ویدئو باشه)؛ اگه
  // نبود، مثل قبل fallback به thumbnail_background.
  const hoverMediaUrl =
    product.thumbnail_hover?.url ||
    (typeof product.thumbnail_background === 'object' && product.thumbnail_background
      ? product.thumbnail_background.url
      : null);

  return (
    <Link
      href={`/shop/product/${product.id}/${product.slug}`}
      className="flex flex-col group"
    >
      <div className="relative aspect-[3/4] overflow-hidden rounded-2xl bg-primary-100 border border-transparent group-hover:border-primary-200 transition-colors duration-500">
        <Image
          src={product.thumbnail.url}
          alt={product.title}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 33vw, 25vw"
          className="object-cover transition-all duration-[900ms] ease-out group-hover:scale-[1.03]"
          priority={priority}
        />

        {hoverMediaUrl && (
          <ProductHoverMedia url={hoverMediaUrl} alt={product.title} />
        )}

        {product.discount_percent > 0 && (
          <span className="absolute top-0 ltr:left-0 rtl:right-0 bg-primary-900 text-primary-0 text-[10px] tracking-[0.14em] uppercase px-2.5 py-1.5">
            -{product.discount_percent}%
          </span>
        )}

        <span className="absolute bottom-3 ltr:right-3 rtl:left-3 size-9 bg-primary-0 border border-primary-900 flex items-center justify-center text-primary-900 opacity-0 translate-y-1.5 transition-all duration-300 ease-out group-hover:opacity-100 group-hover:translate-y-0">
          <Plus className="size-4 stroke-[1.25]" />
        </span>
      </div>

      <div className="pt-4 space-y-1.5">
        <h3 className="text-[12.5px] tracking-wide text-primary-900 truncate">
          {product.title}
        </h3>
        <div className="space-y-1">
          {product.discount_percent > 0 ? (
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className="text-[12px] text-primary-400 line-through">
                {formatPrice(Number(product.price))} {dictionary.ui.product.currency}
              </span>
              <span className="text-primary-900 text-[13px] font-medium">
                {formatPrice(Number(product.final_price))} {dictionary.ui.product.currency}
              </span>
            </div>
          ) : (
            <span className="text-primary-900 text-[13px] font-medium">
              {formatPrice(Number(product.price))} {dictionary.ui.product.currency}
            </span>
          )}
        </div>
        {!!product.product_colors?.length && (
          <div className="flex flex-wrap gap-2 pt-1">
            {product.product_colors.map((color, index) => (
              <Tooltip key={index}>
                <TooltipTrigger asChild>
                  <span>
                    <div
                      className="size-3.5 rounded-full border border-primary-200 cursor-pointer hover:ring-1 hover:ring-offset-1 hover:ring-primary-900 transition-all"
                      style={{ backgroundColor: color.color_code_hex }}
                      title={color.color_title}
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-sm text-primary-0">{color.color_title}</p>
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}